import React, {
  useEffect,
  useReducer,
  useState,
  useContext,
  useMemo,
} from "react"
import ClassNames from "classnames"
import {
  set,
  includes,
  flatten,
  get,
  merge,
  clone,
  size,
  toNumber,
  every,
  map,
  findKey,
  isEqual,
  isEmpty,
  reverse,
  filter,
  has,
  pick,
  find,
  isNull,
} from "lodash"

import { DETAIL_SEARCH_CONDITIONS, KEY_CODE } from "config/const"
import {
  SearchDetailFunction,
  searchStateType,
  useSearchActionReducer,
} from "components/actions/search"
import {
  SearchResultModal,
  ChartSearchResultModal,
  RelativeChartSearchResultModal,
} from "components/organisms/modals/searchResultModal"
import { createRequestBit, CheckHasProperties } from "utils/util"
import { APIResponse } from "redux/apis/types"
import { UserStorage } from "utils/userStorage"
import { SearchCountryTypeContext } from "components/atoms/chart/parts/RelativeSymbolSelector"
import * as Formatter from "utils/Formatter"
import { SearchInput } from "components/atoms/input/input"
import { SearchIcon } from "components/atoms/icon"

// memo Propsの渡し方
// executeFunctionは銘柄をクリックしたタイミングでおこしたい機能。銘柄情報を返します
// checkViewは文字入力中かをbooleanで返します
// forceActiveは必ず一番上のdivにactiveクラスを付与します。それによりspの広げっぱなしの検索枠を作っています
// hasCountryBtnはtrueにすると国選択のアイコンが消えます
//isDirectはtrueでEnter即反映にさせます
//selectCountryは親から選択している国を変える場合に使用

type SearchDetailPropsType = {
  className?: string
  inputClassName?: string
  executeFunction: Function
  checkView?: Function
  initialState?: object
  placeholder: string
  forceActive?: boolean
  hasCountryBtn?: boolean
  isDirect?: boolean
  selectCountry?: undefined | string
  chartType?: number
  collectively?: boolean
  clearButton?: boolean
  setResetFlag?: Function
  selectNewsAll?: boolean
  noMemory?: boolean
  inputId?: string
  focusEffect?: boolean
  setFocusEffect?: Function
  isSearchNews?: boolean
}

//検索用のidつきリスト
const countryList: Array<
  CountryListType
> = DETAIL_SEARCH_CONDITIONS.MARKETS.map((li, key: number) => {
  set(li, "id", key)
  return li
})

const selectedCountry = UserStorage.SelectCountry.get() || ["日本", "米国"]
const defaultSelectedCountry = countryList.filter(li =>
  includes(selectedCountry, li.country)
)
export type CountryListType = {
  country: string
  markets: Array<{
    id: number
    name: string
  }>
  id?: number
}

const searchInitialState: searchStateType = {
  isHover: false,
  isInputting: false,
  isOpenModal: false,
  isSelectSymbol: false,
  isOpenSelectCountry: false,
  value: null,
  searchValue: "",
  selectCountries: defaultSelectedCountry.map(li => li.id),
  selectTab: null,
}

// 銘柄をクリックする際に遅延させるための時間
const releaseSymbolSelectTime = 250

export const SearchDetail: React.FC<SearchDetailPropsType> = props => {
  const {
    searchHistory,
    setSearchHistory,
    searchHistoryMove,
    searchHistorySelectMode,
    setSearchHistorySelectMode,
    searchHistoryHoverNo,
    objectTypeProperties,
  } = useSearchHistory()
  const [onHistory, setOnHistory] = useState(false)
  const [isLoading, load] = useState<boolean>(false)
  const [isDirect, setDirect] = useState<boolean>(false)
  const [currentPage, changePage] = useState<number>(0)
  const [result, setResult] = useState<APIResponse.Res>()
  const [isLongTermEnter, changeLongTermEnter] = useState(false)
  const [searchState, searchReducer] = useReducer(
    useSearchActionReducer(props.executeFunction),
    merge(clone(searchInitialState), props.initialState)
  )
  const searchCountryTypeContext = useContext(SearchCountryTypeContext)
  const focusInput = () => document.getElementById(props.inputId).focus()
  const focusIdList = useMemo(() => {
    return ["search-header", "search-detail-header", "search-chart"]
  }, [])

  const allCountry = flatten(
    DETAIL_SEARCH_CONDITIONS.MARKETS.map(li => li.markets)
  )

  useEffect(() => {
    if (includes(focusIdList, props.inputId) && !!props.focusEffect) {
      focusInput()
    }
    props.setFocusEffect && props.setFocusEffect(false)
  }, [props.focusEffect])

  useEffect(() => {
    if (props.checkView) props.checkView(searchState.isInputting)
  }, [searchState.isInputting])

  useEffect(() => {
    if (!searchState.isOpenModal) {
      setResult({})
      changePage(0)
    }
  }, [searchState.isOpenModal])

  //ホバー時にlocalStorageの内容を登録
  useEffect(() => {
    if (searchState.isHover && !props.noMemory && !searchState.isOpenModal) {
      const storageCountry = map(
        UserStorage.SelectCountry.get(),
        (country: string) => {
          return Number(findKey(countryList, { country: country }))
        }
      )
      if (!isEqual(storageCountry, searchState.selectCountries)) {
        searchReducer({
          type: "SEARCH_SELECT_ALL_COUNTRY",
          allCountry: storageCountry,
        })
      }
    }
  }, [searchState.isHover])

  const doneSearchFromSearchHistory = () => {
    const _searchHistory = reverse([...searchHistory])
    const targetItem = _searchHistory[searchHistoryHoverNo]
    if (CheckHasProperties(targetItem, objectTypeProperties))
      props.executeFunction(_searchHistory[searchHistoryHoverNo])
    if (has(targetItem, "keyword")) {
      searchReducer({
        type: "SEARCH_CHANGE_VALUE",
        value: get(targetItem, "keyword"),
      })
      if (!isLoading) load(true)
      // if (props.isDirect) setDirect(true)
    }
    setSearchHistory(_searchHistory[searchHistoryHoverNo])
    searchHistoryMove(null)
  }

  useEffect(() => {
    if (isLoading) {
      const selectedMarkets: Array<number> = []
      const selectedCountry = countryList.filter(li =>
        includes(searchState.selectCountries, li.id)
      )
      selectedCountry.forEach(country => {
        country.markets.forEach(market => {
          selectedMarkets.push(market.id)
        })
      })
      SearchDetailFunction(
        currentPage,
        searchState.searchValue,
        createRequestBit(selectedMarkets, allCountry.length),
        !!props.chartType
      ).then(data => {
        const resultList = get(data, "ResultList")
        const searchValue = searchState.searchValue.replace(
          /[Ａ-Ｚａ-ｚ０-９]/g,
          function(s) {
            return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
          }
        )

        const isAllSameSymbol = every(resultList, (data: object) => {
          return get(data, "Symbol") === searchValue
        })
        if (!!resultList) {
          setResult(data)
          if (isDirect && size(resultList) === 1) {
            props.executeFunction(resultList[0])
            // 検索した後に、入力データを削除する。
            setSearchHistory(resultList[0])
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: "",
            })
          } else if (props.collectively && isAllSameSymbol) {
            setSearchHistory(searchValue)
            props.executeFunction({ Symbol: searchValue })
            // 検索した後に、入力データを削除する。
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: "",
            })
          } else if (!searchState.isOpenModal) {
            setSearchHistory(searchValue)
            searchReducer({ type: "SEARCH_OPEN_MODAL", action: true })
          }
        }
      })
      load(false)
      setDirect(false)
    }
  }, [isLoading])

  useEffect(() => {
    if (typeof props.selectCountry === "string") {
      searchReducer({
        type: "SEARCH_SELECT_PROPS_COUNTRY",
        allCountry: [toNumber(props.selectCountry)],
      })
    } else {
      searchReducer({
        type: "SEARCH_SELECT_PROPS_COUNTRY",
        allCountry: defaultSelectedCountry.map(li => li.id),
      })
    }
  }, [props.selectCountry])

  useEffect(() => {
    if (!props.selectCountry) {
      if (searchCountryTypeContext.type === 0)
        searchReducer({ type: "SEARCH_SELECT_DOMESTIC" })

      if (searchCountryTypeContext.type === 1)
        searchReducer({
          type: "SEARCH_SELECT_FOREIGN",
        })
    }
  }, [searchCountryTypeContext.type])

  return (
    <div
      className={ClassNames(props.className, {
        active:
          searchState.isHover || searchState.isInputting || props.forceActive,
        "on-history": onHistory,
      })}
      onMouseEnter={() => searchReducer({ type: "SEARCH_HOVER", action: true })}
      onMouseLeave={() =>
        searchReducer({ type: "SEARCH_HOVER", action: false })
      }
    >
      <div className="search-input">
        <input
          id={props.inputId}
          type="text"
          name="search"
          placeholder=""
          autoComplete="off"
          value={searchState.searchValue}
          className={ClassNames(props.inputClassName, {
            active: searchState.searchValue,
          })}
          onChange={e => {
            // 検索履歴から選択するモードを、入力時にはオフにする（上下キーを押すとオンになる）。
            if (searchHistorySelectMode) setSearchHistorySelectMode(false)
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: e.target.value,
            })
          }}
          onFocus={() => {
            searchReducer({ type: "SEARCH_INPUTTING", action: true })
          }}
          onBlur={() => {
            if (searchState.isSelectSymbol) {
              setTimeout(
                () =>
                  searchReducer({ type: "SEARCH_INPUTTING", action: false }),
                releaseSymbolSelectTime
              )
            } else {
              searchReducer({ type: "SEARCH_INPUTTING", action: false })
            }
            // 検索履歴フィルターを消す。
            // onClickの受付時間のために、setTimeoutで遅らせる。
            setTimeout(() => {
              setOnHistory(false)
              setSearchHistorySelectMode(false)
              if (!isNull(searchHistoryHoverNo)) searchHistoryMove(null)
            }, 500)
          }}
          onKeyDown={async e => {
            switch (e.keyCode) {
              // 上キー
              case KEY_CODE.KEY_UP:
                // 上下キーを押した場合、検索履歴から選択するモードにする
                if (!searchHistorySelectMode) setSearchHistorySelectMode(true)
                searchHistoryMove(-1)
                break
              // 下キー
              case KEY_CODE.KEY_DOWN:
                if (searchState.isInputting) setOnHistory(true)
                // 上下キーを押した場合、検索履歴から選択するモードにする
                if (!searchHistorySelectMode) setSearchHistorySelectMode(true)
                searchHistoryMove(1)
                break
              // Enter
              case KEY_CODE.ENTER:
                if (!isLongTermEnter && !searchHistorySelectMode) {
                  changeLongTermEnter(true)
                  if (props.clearButton && isEmpty(searchState.searchValue)) {
                    props.setResetFlag && props.setResetFlag(true)
                  } else {
                    if (!isLoading) load(true)
                    if (props.isDirect) setDirect(true)
                  }
                }
                if (searchHistorySelectMode) {
                  doneSearchFromSearchHistory()
                }
                const _e = document.activeElement as HTMLElement
                _e.blur()
                break
              default:
                break
            }
          }}
          onKeyUp={() => changeLongTermEnter(false)}
        />
        <div
          className={ClassNames("floating-label", { "on-history": onHistory })}
        >
          {props.placeholder}
        </div>
        {onHistory && (
          <SearchHistory
            historyData={searchHistory}
            clickFn={() => {
              doneSearchFromSearchHistory()
            }}
            onHide={() => setOnHistory(false)}
            hoverNo={searchHistoryHoverNo}
            setHoverNo={searchHistoryMove}
          />
        )}
        {props.clearButton && (
          <i
            className="material-icons ripple clear-icon"
            onClick={() => {
              if (
                !props.selectNewsAll ||
                !isEmpty(searchState.searchValue) ||
                props.isSearchNews
              ) {
                props.setResetFlag(true)
              }
              searchReducer({
                type: "SEARCH_CHANGE_VALUE",
                value: "",
              })
            }}
          >
            clear
          </i>
        )}
      </div>
      <div className="search-icon-group">
        <div title={"履歴"} className="icon-title">
          {onHistory ? (
            <i
              className="material-icons ripple search-arrow-icon"
              onClick={() => {
                setOnHistory(false)
              }}
            >
              keyboard_arrow_up
            </i>
          ) : (
            <i
              className="material-icons ripple search-arrow-icon"
              onClick={() => {
                //focus中に履歴を開くとonblurが効いてしまい履歴を閉じてしまうので、遅らせて発火させる
                setTimeout(() => {
                  setOnHistory(true)
                  focusInput()
                }, 500)
              }}
            >
              keyboard_arrow_down
            </i>
          )}
        </div>
        <div title={"検索"} className="icon-title">
          <i
            className="material-icons ripple search-icon"
            onClick={() => {
              if (!isLoading) load(true)
            }}
          >
            search
          </i>
        </div>
        {props.hasCountryBtn ? null : (
          <div title={"国選択"} className="icon-title">
            <i
              className="material-icons ripple global-icon"
              onClick={() => searchReducer({ type: "SEARCH_OPEN_COUNTRY" })}
            >
              location_on
            </i>
          </div>
        )}
      </div>
      {!props.chartType ? (
        <SearchResultModal
          state={searchState}
          show={searchState.isOpenModal}
          result={result}
          onHide={() => {
            searchReducer({ type: "SEARCH_OPEN_MODAL", action: false })
            if (!props.noMemory) {
              const _selectCountry = map(
                searchState.selectCountries,
                (countryNum: number) => {
                  return countryList[countryNum].country
                }
              )
              UserStorage.SelectCountry.set(_selectCountry)
            }
          }}
          selectCountry={(id: number) => {
            searchReducer({ type: "SEARCH_SELECT_COUNTRY", selectCountry: id })
          }}
          selectAllCountry={() =>
            searchReducer({
              type: "SEARCH_SELECT_ALL_COUNTRY",
              allCountry: countryList.map(li => li.id),
            })
          }
          changeSearchValue={(str: string) =>
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: str,
            })
          }
          load={() => load(true)}
          countryList={countryList}
          currentPage={currentPage}
          changePage={(page: number) => changePage(page)}
          execute={(data: APIResponse.Res) => {
            searchReducer({
              type: "SEARCH_EXECUTE",
              symbol: data,
            })
            // setSearchHistory(data)
            // 検索した後に、入力データを削除する。
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: "",
            })
            if (!props.noMemory) {
              const _selectCountry = map(
                searchState.selectCountries,
                (countryNum: number) => {
                  return countryList[countryNum].country
                }
              )
              UserStorage.SelectCountry.set(_selectCountry)
            }
          }}
        />
      ) : props.chartType === 1 ? (
        <ChartSearchResultModal
          state={searchState}
          show={searchState.isOpenModal}
          result={result}
          onHide={() =>
            searchReducer({ type: "SEARCH_OPEN_MODAL", action: false })
          }
          selectDomistic={() =>
            searchReducer({ type: "SEARCH_SELECT_DOMESTIC" })
          }
          selectForeign={() => searchReducer({ type: "SEARCH_SELECT_FOREIGN" })}
          changeSearchValue={(str: string) =>
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: str,
            })
          }
          load={() => load(true)}
          countryList={countryList}
          currentPage={currentPage}
          changePage={(page: number) => changePage(page)}
          execute={(data: APIResponse.Res) => {
            searchReducer({
              type: "SEARCH_EXECUTE",
              symbol: data,
            })
            // setSearchHistory(data)
            // 検索した後に、入力データを削除する。
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: "",
            })
          }}
        />
      ) : (
        <RelativeChartSearchResultModal
          state={searchState}
          show={searchState.isOpenModal}
          result={result}
          onHide={() =>
            searchReducer({ type: "SEARCH_OPEN_MODAL", action: false })
          }
          selectDomistic={() =>
            searchReducer({ type: "SEARCH_SELECT_DOMESTIC" })
          }
          selectForeign={() => searchReducer({ type: "SEARCH_SELECT_FOREIGN" })}
          changeSearchValue={(str: string) =>
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: str,
            })
          }
          load={() => load(true)}
          countryList={countryList}
          currentPage={currentPage}
          changePage={(page: number) => changePage(page)}
          execute={(data: APIResponse.Res) => {
            searchReducer({
              type: "SEARCH_EXECUTE",
              symbol: data,
            })
            setSearchHistory(data)
            // 検索した後に、入力データを削除する。
            searchReducer({
              type: "SEARCH_CHANGE_VALUE",
              value: "",
            })
          }}
        />
      )}
    </div>
  )
}

export const SearchHistory = (props: {
  historyData: Array<
    | {
        Symbol: string
        SymbolName: string
        SymbolNameS: string
        Exchange: number
        SecurityType: number
      }
    | { keyword: string }
    | {}
  >
  clickFn: Function
  onHide: Function
  hoverNo: number
  setHoverNo: Function
}) => {
  const { historyData, clickFn, onHide, hoverNo, setHoverNo } = props
  // 新しいデータが配列の最後になるため、reverseで順番を逆にする。
  const _historyData = useMemo(() => {
    if (!historyData) return []
    return reverse([...historyData])
  }, [historyData])

  return (
    <>
      {!isEmpty(_historyData) && (
        <div className="search-history">
          {map(_historyData, (items, key) => {
            return (
              <div
                key={key}
                onClick={() => {
                  clickFn(items)
                  onHide()
                }}
                className="search-history-item"
                onMouseMove={() => {
                  hoverNo === key || setHoverNo(key)
                }}
                onMouseEnter={() => setHoverNo(key)}
              >
                <div className="search-history-display-wrapper">
                  {!get(items, "keyword") && (
                    <span className="search-history-display-item symbol-code-label">
                      {Formatter.symbol(items)}
                    </span>
                  )}
                  <span className="search-history-display-item">
                    {get(items, "SymbolNameS") ||
                      get(items, "SymbolName") ||
                      get(items, "keyword")}
                  </span>
                </div>
                {hoverNo === key && <span className="hover-bar"></span>}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
// keyword検索用（銘柄検索等に使用されている）。
export const SearchKeyword = (props: {
  keyword: string
  setKeyword: Function
  execute: Function
  inputId?: string
}) => {
  const { keyword, setKeyword, execute } = props
  const [onHistory, setOnHistory] = useState(false)
  const focusInput = () => document.getElementById(props.inputId).focus()

  const {
    searchHistory,
    setSearchHistory,
    searchHistoryMove,
    searchHistorySelectMode,
    setSearchHistorySelectMode,
    searchHistoryHoverNo,
  } = useSearchHistory()

  const doneSearchFromSearchHistory = () => {
    const _searchHistory = reverse([...searchHistory])
    const targetItem = _searchHistory[searchHistoryHoverNo]
    if (!get(targetItem, "keyword") && !get(targetItem, "Symbol")) return
    setKeyword(get(targetItem, "keyword") || get(targetItem, "Symbol"))
    setSearchHistory(targetItem)
    searchHistoryMove(null)
    execute()
  }

  return (
    <div className="search-keyword">
      <div className="search-items">
        <SearchInput
          inputId={props.inputId}
          value={keyword || ""}
          className="filter-control-item floating-input-field"
          onChange={(str: string) => {
            // 検索履歴から選択するモードを、入力時にはオフにする（上下キーを押すとオンになる）。
            if (searchHistorySelectMode) setSearchHistorySelectMode(false)
            setKeyword(str)
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            switch (e.keyCode) {
              // 上キー
              case KEY_CODE.KEY_UP:
                // 上下キーを押した場合、検索履歴から選択するモードにする
                if (!searchHistorySelectMode) setSearchHistorySelectMode(true)
                searchHistoryMove(-1)
                break
              // 下キー
              case KEY_CODE.KEY_DOWN:
                // 上下キーを押した場合、検索履歴から選択するモードにする
                if (!searchHistorySelectMode) setSearchHistorySelectMode(true)
                searchHistoryMove(1)
                break
              // Enter
              case KEY_CODE.ENTER:
                if (!searchHistorySelectMode) {
                  execute()
                  setSearchHistory(keyword)
                }
                if (searchHistorySelectMode) {
                  doneSearchFromSearchHistory()
                }
                const _e = document.activeElement as HTMLElement
                _e.blur()
                break
              default:
                break
            }
          }}
          onBlur={() => {
            // 検索履歴フィルターを消す。
            // onClickの受付時間のために、setTimeoutで遅らせる。
            setTimeout(() => {
              setOnHistory(false)
              setSearchHistorySelectMode(false)
              if (!isNull(searchHistoryHoverNo)) searchHistoryMove(null)
            }, 500)
          }}
        />
        <div title={"履歴"} className="icon-title">
          {onHistory ? (
            <i
              className="material-icons ripple search-arrow-icon"
              onClick={() => {
                setOnHistory(false)
              }}
            >
              keyboard_arrow_up
            </i>
          ) : (
            <i
              className="material-icons ripple search-arrow-icon"
              onClick={() => {
                //focus中に履歴を開くとonblurが効いてしまい履歴を閉じてしまうので、遅らせて発火させる
                setTimeout(() => {
                  setOnHistory(true)
                  focusInput()
                }, 500)
              }}
            >
              keyboard_arrow_down
            </i>
          )}
        </div>
        <SearchIcon onClick={() => execute()} />
      </div>
      {onHistory && (
        <SearchHistory
          historyData={searchHistory}
          clickFn={() => {
            doneSearchFromSearchHistory()
          }}
          onHide={() => setOnHistory(false)}
          hoverNo={searchHistoryHoverNo}
          setHoverNo={searchHistoryMove}
        />
      )}
    </div>
  )
}

// 検索履歴の処理群。
const useSearchHistory = () => {
  const _searchHistory = UserStorage.searchHistory.get()
  const objectTypeProperties = useMemo(() => {
    return [
      "Symbol",
      "SymbolName",
      "SymbolNameS",
      "Exchange",
      "SecurityType",
      "Ticker",
      "NumberOfDecimals",
    ]
  }, [])
  const [searchHistory, setSearchHistory] = useState<
    Array<
      | {
          Symbol: string
          SymbolName: string
          SymbolNameS: string
          Exchange: number
          SecurityType: number
          Ticker: string
          NumberOfDecimals: number
        }
      | { keyword: string }
      | {}
    >
  >(
    (() => {
      const data = UserStorage.searchHistory.get() || []
      if (UserStorage.searchHistory.cheackData(data)) return data
      UserStorage.searchHistory.remove()
      return []
    })()
  )
  // 上下キーで、検索履歴を操作する用。
  const [searchHistoryHoverNo, setSearchHistoryHoverNo] = useState(null)
  // 検索履歴から銘柄を選択するモード用フラグ。Enterが入力されたデータを検索する動作と、検索履歴から選ぶ動作で競合するため。
  const [searchHistorySelectMode, setSearchHistorySelectMode] = useState(false)

  const _setSearchHistory = (data: object | string) => {
    let _searchHistory = clone(searchHistory) || []
    let _data: object = null
    if (typeof data === "string") {
      if (!data) return
      _data = { keyword: data }
    }
    if (typeof data === "object") {
      if (
        isEmpty(data) ||
        (!CheckHasProperties(data, objectTypeProperties) &&
          !has(data, "keyword"))
      )
        return
      _data = isEmpty(pick(data, objectTypeProperties))
        ? pick(data, "keyword")
        : pick(data, objectTypeProperties)
    }
    // 以前に同じ検索履歴があった場合、履歴から削除する。=>最後に加えることで、履歴の順番を入れ替える。
    if (!!find(_searchHistory, _data)) {
      _searchHistory = filter(
        _searchHistory,
        (items, id) => !isEqual(items, _data)
      )
    }
    // 10以上ある場合、最初のデータ削除。
    if (_searchHistory.length >= 10) _searchHistory.shift()
    _searchHistory.push(_data)
    setSearchHistory(_searchHistory)
  }

  const searchHistoryMove = (num: number | null) => {
    if (isNull(num)) return setSearchHistoryHoverNo(null)
    const max = searchHistory.length - 1
    if (num === 0) {
      return setSearchHistoryHoverNo(0)
    }
    let _searchHistoryHoverNo = searchHistoryHoverNo
    // 初期は、nullのため0 or maxを代入。
    if (isNull(_searchHistoryHoverNo)) {
      if (num > 0) return setSearchHistoryHoverNo(0)
      if (num < 0) return setSearchHistoryHoverNo(max)
    }
    _searchHistoryHoverNo += num
    if (_searchHistoryHoverNo < 0) _searchHistoryHoverNo = max
    if (_searchHistoryHoverNo >= searchHistory.length) _searchHistoryHoverNo = 0
    setSearchHistoryHoverNo(_searchHistoryHoverNo)
  }

  //それぞれの履歴から、変更されるためUserStorageとStateの両方を監視している。
  //Stateが変更されるのに、時間がかかるため、setTimeoutを入れないと、無限ループになる。
  useEffect(() => {
    if (!isEqual(_searchHistory, searchHistory))
      setTimeout(() => UserStorage.searchHistory.set(searchHistory), 100)
  }, [searchHistory])

  useEffect(() => {
    if (!isEqual(_searchHistory, searchHistory) && !isEmpty(_searchHistory))
      setSearchHistory(_searchHistory)
  }, [_searchHistory])

  return {
    searchHistory,
    setSearchHistory: _setSearchHistory,
    searchHistoryMove,
    searchHistorySelectMode,
    setSearchHistorySelectMode,
    searchHistoryHoverNo,
    objectTypeProperties,
  }
}
